import { TextStyle } from 'react-native'
import { colors, typography } from '../../../../doclets/tokens'

// CSS for Draft.js's editor, generated from the same doclet tokens the
// read-only RichTextView uses, so text looks the same while editing as it does
// in the preview. Draft.js renders DOM, so it needs CSS rather than RN styles.

export const TEXT_EDITOR_CLASS = 'doclet-text'
export const WIDE_CLASS = 'doclet-text--wide'

// Draft.js block type -> class applied by blockStyleFn
export const blockClass = (type: string) => {
  switch (type) {
    case 'heading':
      return 'doclet-text-heading'
    case 'sub-heading':
      return 'doclet-text-subheading'
    case 'blockquote':
      return 'doclet-text-quote'
    case 'small':
      return 'doclet-text-small'
    case 'unordered-list-item':
    case 'ordered-list-item':
      return 'doclet-text-item'
    default:
      return 'doclet-text-body'
  }
}

const px = (value?: number | string) =>
  typeof value === 'number' ? `${value}px` : value ?? 'inherit'

const declarations = (style: TextStyle, includeMargin = true) =>
  [
    `font-family: ${style.fontFamily};`,
    `font-weight: ${style.fontWeight};`,
    `color: ${style.color};`,
    `font-size: ${px(style.fontSize)};`,
    `line-height: ${px(style.lineHeight)};`,
    includeMargin ? `margin: 0 0 ${px(style.marginBottom)};` : ''
  ].join(' ')

const rulesFor = (isWide: boolean) => {
  const scope = isWide
    ? `.${TEXT_EDITOR_CLASS}.${WIDE_CLASS}`
    : `.${TEXT_EDITOR_CLASS}`
  const body = typography.body(isWide)
  const list = typography.list(isWide)
  return `
    ${scope} .doclet-text-body { ${declarations(body)} }
    ${scope} .doclet-text-heading { ${declarations(
    typography.heading(isWide)
  )} }
    ${scope} .doclet-text-subheading { ${declarations(
    typography.subheading(isWide)
  )} }
    ${scope} .doclet-text-small { ${declarations(typography.small(isWide))} }
    ${scope} .doclet-text-quote {
      ${declarations(body)}
      border-left: 2px solid ${colors.rule};
      padding-left: ${px(body.fontSize)};
    }
    ${scope} .doclet-text-item {
      ${declarations(body, false)}
      margin: ${px(list.itemSpacing)} 0 0;
    }
    ${scope} .public-DraftStyleDefault-ul,
    ${scope} .public-DraftStyleDefault-ol {
      margin: 0 0 ${px(body.marginBottom)};
      padding: 0;
    }
    ${scope} .public-DraftStyleDefault-depth0.public-DraftStyleDefault-listLTR {
      margin-left: ${px(list.indent)};
    }
    ${scope} .public-DraftEditorPlaceholder-inner {
      ${declarations(body, false)}
      color: #AFAEAD; /* --w50-ink-400 */
    }
  `
}

export const TEXT_EDITOR_CSS = `
  .${TEXT_EDITOR_CLASS} .doclet-text-body [style*="font-weight: bold"],
  .${TEXT_EDITOR_CLASS} .doclet-text-quote [style*="font-weight: bold"],
  .${TEXT_EDITOR_CLASS} .doclet-text-item [style*="font-weight: bold"] {
    font-weight: ${typography.bold.fontWeight};
  }
  .${TEXT_EDITOR_CLASS} .public-DraftEditorPlaceholder-root { position: absolute; }
  /* Undo MI6's global RichTextEditor rule (components/RichTextEditor/
     draft-js.css), which makes every Draft.js root overflow: scroll -- with
     always-visible scroll bars that draws empty tracks around each block. */
  .${TEXT_EDITOR_CLASS} .DraftEditor-root {
    height: auto;
    overflow: visible;
    max-width: none;
  }
  ${rulesFor(false)}
  ${rulesFor(true)}
`

let injected = false

/** Adds the editor CSS to the page once. */
export const injectTextEditorStyles = () => {
  if (injected || typeof document === 'undefined') return
  const style = document.createElement('style')
  style.id = 'doclet-text-editor-styles'
  style.textContent = TEXT_EDITOR_CSS
  document.head.appendChild(style)
  injected = true
}
